import { useState, type ReactNode, type JSX } from 'react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid2'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { PRESETS, type JobSettings } from '@profiler/shared'
import { COUNTRY_OPTIONS, presetOptions } from './state'

function Section({
  title,
  description,
  children,
}: Readonly<{
  title: string
  description?: string
  children: ReactNode
}>): JSX.Element {
  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2">{title}</Typography>
        {description ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {description}
          </Typography>
        ) : null}
        {children}
      </CardContent>
    </Card>
  )
}

/**
 * Every switch on this step carries a caption explaining what it costs, and the
 * caption has to hang under the label rather than under the switch, so the pair
 * is one component: five hand-tuned indents drifted into three different
 * vertical rhythms on a single card.
 */
function SwitchWithHelp({
  checked,
  onChange,
  label,
  help,
}: Readonly<{
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  help: string
}>): JSX.Element {
  return (
    <Box>
      <FormControlLabel
        control={<Switch checked={checked} onChange={(event) => onChange(event.target.checked)} />}
        label={label}
      />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
        {help}
      </Typography>
    </Box>
  )
}

/**
 * ConfirmJobRequestSchema caps the name here, and confirm does not run until the
 * review step, so an over-long name is only refused two steps after it is typed.
 */
export const MAX_NAME_LENGTH = 120

/** Bounds mirror JobSettingsSchema so the form cannot produce a body zod rejects. */
function clampInt(raw: string, min: number, max: number, fallback: number): number {
  const parsed = Number.parseInt(raw, 10)
  if (Number.isNaN(parsed)) return fallback
  return Math.min(max, Math.max(min, parsed))
}

export function StepSettings({
  name,
  onNameChange,
  settings,
  onChange,
}: Readonly<{
  name: string
  onNameChange: (name: string) => void
  settings: JobSettings
  onChange: (settings: JobSettings) => void
}>): JSX.Element {
  const patch = (part: Partial<JobSettings>): void => onChange({ ...settings, ...part })
  const [rejectedCountry, setRejectedCountry] = useState<string | null>(null)

  return (
    <Stack spacing={2}>
      <Section title="Job">
        <TextField
          label="Job name"
          fullWidth
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          slotProps={{ htmlInput: { maxLength: MAX_NAME_LENGTH } }}
          helperText={`Shown in the jobs list. Defaults to the file name. ${name.length}/${MAX_NAME_LENGTH}`}
        />
      </Section>

      <Section
        title="Who to look for"
        description="Presets are size-tiered: on the real sample the technology group contributed nobody the founder group had not already found, while still paying for rejected profiles."
      >
        <Stack spacing={2}>
          <TextField
            select
            label="Persona preset"
            fullWidth
            value={settings.presetId}
            onChange={(event) => {
              const presetId = event.target.value
              const preset = PRESETS[presetId]
              // Personas travel with the preset. Changing the id without the
              // personas would run the previous preset's queries under a new name.
              patch(preset ? { presetId, personas: preset.personas } : { presetId })
            }}
          >
            {presetOptions().map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {settings.personas.map((persona) => (
              <Chip
                key={persona.key}
                size="small"
                variant="outlined"
                label={`${persona.label} · page size ${persona.pageSize}`}
              />
            ))}
          </Stack>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="People per company"
                type="number"
                fullWidth
                value={settings.minPeople}
                slotProps={{ htmlInput: { min: 1, max: 10 } }}
                onChange={(event) =>
                  patch({ minPeople: clampInt(event.target.value, 1, 10, settings.minPeople) })
                }
                helperText="The ladder stops once this many people pass the gates."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                multiple
                freeSolo
                options={[...COUNTRY_OPTIONS]}
                value={settings.expectedCountries}
                onChange={(_event, value) => {
                  const codes = value.map((code) => code.trim().toUpperCase())
                  // The schema takes ISO-2 only, so 'USA' cannot be kept, but
                  // dropping it in silence is worse than refusing it: no chip
                  // appears, nothing says why, and the user reads that as the
                  // country having been applied when the gate is now off.
                  setRejectedCountry(codes.find((code) => code.length !== 2) ?? null)
                  patch({ expectedCountries: codes.filter((code) => code.length === 2) })
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Expected countries"
                    error={rejectedCountry !== null}
                    helperText={
                      rejectedCountry === null
                        ? 'ISO-2 codes. Only applied when the company match is weaker than a URL match.'
                        : `${rejectedCountry} is not an ISO-2 code — the United States is US, the United Kingdom is GB.`
                    }
                  />
                )}
              />
            </Grid>
          </Grid>

          <SwitchWithHelp
            checked={settings.includeDirector}
            onChange={(checked) => patch({ includeDirector: checked })}
            label="Include Director titles"
            help="Owner-speak in Indian and UK SMBs, noise in enterprises. On by default."
          />
        </Stack>
      </Section>

      <Section title="What to fetch">
        <Stack spacing={2}>
          <SwitchWithHelp
            checked={settings.includeCompanyProfile}
            onChange={(checked) => patch({ includeCompanyProfile: checked })}
            label="Include the company profile"
            help="Adds industry, size and headquarters. Company Lookup is charged even when it finds nothing."
          />

          <SwitchWithHelp
            checked={settings.enableFetch}
            onChange={(checked) => patch({ enableFetch: checked })}
            label="Resolve websites and harvest LinkedIn URLs"
            help="Rungs 0 and 0b. Zero credits, and the only thing that rescues a redirected or expired domain. Turning it off makes dead domains fail silently."
          />

          <SwitchWithHelp
            checked={settings.autoWorkEmail}
            onChange={(checked) => patch({ autoWorkEmail: checked })}
            label="Look up a work email for every contact found"
            help="Off by default: it costs credits per contact whether or not an address exists. Leave it off and request emails for the contacts you actually want."
          />

          <SwitchWithHelp
            checked={settings.forceRefresh === true}
            onChange={(checked) => patch({ forceRefresh: checked })}
            label="Re-profile companies already found recently"
            help="Off by default. A company already searched with this preset in the last 180 days is replayed from storage at zero credits. Turn this on only to deliberately refresh a stale list, because a repeat search is billed exactly like the first one."
          />
        </Stack>
      </Section>

      <Section
        title="Credit caps"
        description="Hard ceilings. The worker stops the moment either is reached."
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Per company"
              type="number"
              fullWidth
              value={settings.creditCapPerCompany}
              slotProps={{ htmlInput: { min: 1, max: 200 } }}
              onChange={(event) =>
                patch({
                  creditCapPerCompany: clampInt(event.target.value, 1, 200, settings.creditCapPerCompany),
                })
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Whole job"
              type="number"
              fullWidth
              value={settings.creditCapTotal}
              slotProps={{ htmlInput: { min: 1 } }}
              onChange={(event) =>
                patch({ creditCapTotal: clampInt(event.target.value, 1, 1_000_000, settings.creditCapTotal) })
              }
            />
          </Grid>
        </Grid>
      </Section>

      {settings.autoWorkEmail ? (
        <Alert severity="warning">
          Automatic work email is on. Every contact found will be billed for a lookup, including the ones you
          would never have mailed.
        </Alert>
      ) : null}

      {settings.expectedCountries.length === 0 ? (
        <Alert severity="warning">
          No expected countries, so the country gate is off entirely: a profile from anywhere is accepted
          whenever the company matched on something weaker than its URL.
        </Alert>
      ) : null}
    </Stack>
  )
}
